// src/store/retinolStore.ts
// 🎯 Главный Store - комбинация всех slices

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Импортируем все slices
import { createCalendarSlice, type CalendarSlice } from './slices/calendarSlice';
import { createAchievementsSlice, type AchievementsSlice } from './slices/achievementsSlice';
import { createProfileSlice, type ProfileSlice } from './slices/profileSlice';
import { createQuestionnaireSlice, type QuestionnaireSlice } from './slices/questionnaireSlice';
import { createSyncSlice, type SyncSlice } from './slices/syncSlice';

// Экспортируем типы из slices
export type {
  Application,
  MissedDaysStats,
  Statistics,
} from './slices/calendarSlice';
export type { QuestionnaireState } from './slices/questionnaireSlice';

// ========== ОБЪЕДИНЁННЫЙ ТИП STORE ==========

export type RetinolStore = CalendarSlice &
  AchievementsSlice &
  ProfileSlice &
  QuestionnaireSlice &
  SyncSlice & {
    // Дополнительные методы
    reset: () => void;
  };

// ========== СОЗДАНИЕ STORE ==========

export const useRetinolStore = create<RetinolStore>()(
  persist(
    (...a) => ({
      // Комбинируем все slices
      ...createCalendarSlice(...a),
      ...createAchievementsSlice(...a),
      ...createProfileSlice(...a),
      ...createQuestionnaireSlice(...a),
      ...createSyncSlice(...a),

      // Общий reset для всего store
      reset: () => {
        const[, , api] = a;
        const state = api.getState();

        state.resetCalendar();
        state.resetAchievements();
        state.resetProfile();
        state.resetQuestionnaire();
        state.resetSync();

        console.log('✅ Store полностью сброшен');
      },
    }),
    {
      name: 'retinol-storage',
      // Указываем, какие поля сохранять в persist
      partialize: (state) => ({
        // Calendar
        plannedDates: state.plannedDates,
        startDate: state.startDate,
        applications: state.applications,
        missedDays: state.missedDays,
        currentStreak: state.currentStreak,

        // Achievements (только stage, БЕЗ newAchievements - это runtime поле!)
        currentStage: state.currentStage,
        // ✅ FIX БАГ 1: Сохраняем toastsShown чтобы не показывать тосты повторно при перезапуске
        toastsShown: state.toastsShown,
        // ❌ newAchievements: state.newAchievements, - УДАЛЕНО! Это вызывало повторные тосты

        // Profile
        shareCount: state.shareCount,
        referralCount: state.referralCount,
        feedbackCount: state.feedbackCount,
        lastFeedbackPrompt: state.lastFeedbackPrompt,
        upgradePromptShown: state.upgradePromptShown,
        upgradeRemindDate: state.upgradeRemindDate,

        // Questionnaire
        questionnaire: state.questionnaire,
        contraindication: state.contraindication,

        // Sync
        recommendedProducts: state.recommendedProducts,
        lastSync: state.lastSync,
        useRetinolBeforeBackend: state.useRetinolBeforeBackend,
        // НЕ сохраняем runtime-поля:
        // isCheckingAchievements, achievementsCache, lastAchievementsCheck, newAchievements
      }),
    }
  )
);

// ========== ХЕЛПЕРЫ (ре-экспорт) ==========

export { daysBetween } from './slices/calendarSlice';